'use strict'

// "Your agents" home-row data + the Create Agent submit.
//
// Assignment is the server-backed AgentAssignment model (see
// functions/agentAssignments.js): an agent is "assigned to" a person when
// an AgentAssignment row exists. agentsAssignedToMe delegates there. The
// agent roster comes from root._agents (filled by sdk.listAgents via the
// embedded canvas), with a graceful empty fallback.

import { peopleStatus } from './homeLoadState.js'
import {
  agentsAssignedTo,
  assignAgentToUser,
  agentsVisibleToViewer,
  viewerTier
} from './agentAssignments.js'

// The current user's canvas node id. Matches team-and-agents.js _personId
// (userId || email) so the edge endpoints line up.
export const myPersonId = (root) => root?.currentUserId || root?.currentUserEmail || null

const _agentRoster = (root) => (Array.isArray(root?._agents) ? root._agents : [])

// FALSE-ZERO-1 surface 4 (consumer half) — tri-state read of the agent
// roster, matching the producer contract declared in
// `shared/state.js:142-147` (shared is read-only from this package; this
// mirrors it, it does not alter it):
//   root._agents === null && root._agentsError === null  → still loading
//   root._agents === null && root._agentsError !== null  → load failed
//   Array.isArray(root._agents)                           → loaded
//                                                            ([] is a real,
//                                                            verified zero)
// Every consumer below reads the roster through this instead of a bare
// `Array.isArray(_agents) ? length : 0`, so a still-loading or failed
// roster can never render as the same "0" a genuinely agent-less
// workspace shows.
export const agentRosterStatus = (root) => {
  if (Array.isArray(root?._agents)) return 'loaded'
  if (root?._agentsError) return 'error'
  return 'loading'
}

// Agents assigned to the current user — sourced from the server-backed
// AgentAssignment model (root.agentAssignmentsByAssignee) via
// agentsAssignedTo, replacing the old per-user canvas connection graph.
export const agentsAssignedToMe = (root) => agentsAssignedTo(root, root?.currentUserId)

// Children for the YourAgents widget grid: ONLY the agents assigned to
// the current user (per Nika 2026-06-03 — "should be showing agents
// connected to me"), followed by the trailing "Create agent" tile.
// `_agent` / `_create` let the card render the two shapes differently.
// Unassigned roster agents live on /team-and-agents — they don't belong
// in the "your agents" surface.
export const yourAgentsRows = (root) => {
  // Tier-aware (shared with /my-agents via agentsVisibleToViewer): an
  // admin/owner sees every agent, a manager sees agents assigned to self +
  // reports, a member sees agents assigned to self. `_assigned` is only
  // true for agents actually assigned to the viewer (carries an
  // assignmentId) so the relationship badge shows on those but not on the
  // admin's full-roster view.
  const agents = agentsVisibleToViewer(root)
  return [
    ...agents.map((a) => ({ ...a, _agent: true, _assigned: !!a._assignmentId })),
    { _create: true, id: '__create_agent__', name: 'Create agent' }
  ]
}

// Header subtitle for the widget.
//
// FALSE-ZERO-1 surface 4: only the admin branch reads straight off
// `root._agents` (agentsVisibleToViewer's admin path is the full roster) —
// the manager/member branch counts `root.agentAssignmentsByAssignee`
// instead, a separately-loaded map with its own state, so it is not part
// of this ambiguity and is left untouched. For admin, "No agents yet" is
// only correct once the roster has actually loaded and is genuinely empty.
// Reached as `el.call('yourAgentsSubtitle', root)`, so `this` is the element.
// A direct import call (the unit tests) has no element; `_t` returns the KEY
// there instead of throwing, and those tests pin the key.
const _t = function (key) {
  return this && typeof this.call === 'function' ? this.call('polyglot', key) : key
}
export const yourAgentsSubtitle = function (root) {
  const t = _t.bind(this)
  if (viewerTier(root) === 'admin') {
    const status = agentRosterStatus(root)
    if (status === 'loading') return t('workspace.agents.checking')
    if (status === 'error') return t('workspace.agents.loadFailed')
    const n = agentsVisibleToViewer(root).length
    if (!n) return t('workspace.agents.none')
    return t(
      n === 1 ? 'workspace.agents.countInWorkspaceOne' : 'workspace.agents.countInWorkspace'
    ).replace('{count}', n)
  }
  const n = agentsVisibleToViewer(root).length
  if (n > 0) {
    return t(
      n === 1 ? 'workspace.agents.assignedToYouOne' : 'workspace.agents.assignedToYou'
    ).replace('{count}', n)
  }
  return t('workspace.agents.createFirst')
}

// Numeral text for the AppAgentsWidget home-row Count (same tri-state
// contract as yourAgentsSubtitle above, admin-only — see the comment
// there). Non-admin counts are unaffected: they read
// `root.agentAssignmentsByAssignee`, not `root._agents`.
export const yourAgentsCountText = (root) => {
  if (viewerTier(root) === 'admin') {
    const status = agentRosterStatus(root)
    if (status === 'loading') return '–'
    if (status === 'error') return '!'
  }
  return String(yourAgentsRows(root).filter((r) => r._agent).length)
}

// Numeral text for the org-wide "AI agents" count (AgentsWidget's
// RowAgents.Num on Home) — this one always reads the raw roster length
// (no tier filter), so it needs the tri-state guard unconditionally.
export const agentRosterCountText = (root) => {
  const status = agentRosterStatus(root)
  if (status === 'loading') return '–'
  if (status === 'error') return '!'
  return String(_agentRoster(root).length)
}

// ── FALSE-ZERO-1 residual: the three Home consumers that still read
// `_agents.length` raw (AppTeamWidget's combined count, the AgentsWidget
// share bar, the AgentsWidget avatar row). Measured live on
// `/w/system/hq` 2026-08-20 with the roster absent: the count rendered a
// confident "78" (people only — the true total is 86) and the share bar
// rendered a confident 100% / 0% split. Both are MEASUREMENTS, so both
// may only render from positively-read inputs.
//
// The people side reads the Home load-state contract
// (FALSE-ZERO-HOME-NON-AGENT-KEYS-1, functions/homeLoadState.js): `people`
// is `[]`-seeded, so `Array.isArray(root.people)` is true from the first
// frame and never meant "loaded" — a loaded 2-agent roster against the
// seeded `[]` rendered a confident "2" here. `peopleLoaded` is the positive
// marker every people producer writes; `peopleError` is its failure.
const _peopleLoaded = (root) => peopleStatus(root) === 'loaded'
const _peopleFailed = (root) => peopleStatus(root) === 'error'

// Combined "team & agents" headcount. A sum is only as honest as its
// least-known term: an absent roster made the total silently short by the
// whole agent population, which reads as a verified number, not as a gap.
export const teamHeadcountText = (root) => {
  const status = agentRosterStatus(root)
  if (status === 'error' || _peopleFailed(root)) return '!'
  if (status === 'loading' || !_peopleLoaded(root)) return '–'
  return String(root.people.length + _agentRoster(root).length)
}

// Segmented people-vs-agents share bar. A PROPORTION is the worst thing to
// render from an unread input — an absent count reads as an empty state,
// but a wrong fraction reads as a confident measurement. While either side
// is unread both segments collapse to 0 and the track shows unsegmented:
// "the split is not known". A genuinely empty workspace (loaded, total 0)
// keeps the old neutral 50/50 — that is a real, verified zero.
export const teamSplitFlex = (root, part) => {
  if (teamSplitUnknown(root)) return '0'
  const people = root.people.length
  const agents = _agentRoster(root).length
  const total = people + agents
  if (!total) return '0.5'
  const frac = (part === 'agents' ? agents : people) / total
  return String(frac > 0 ? Math.max(frac, 0.04) : 0)
}

// Is the people-vs-agents split unknown? Drives which of the two bars the
// AgentsWidget mounts: the real segmented bar, or the design system's
// skeleton placeholder. An unsegmented track on its own still reads as
// "empty", so the unknown state gets a real loading affordance rather than
// a silently collapsed bar.
export const teamSplitUnknown = (root) =>
  agentRosterStatus(root) !== 'loaded' || !_peopleLoaded(root)

export const teamSplitTitle = function (root) {
  const t = _t.bind(this)
  if (agentRosterStatus(root) === 'error') return t('workspace.agents.loadFailedShort')
  if (_peopleFailed(root)) return t('workspace.agents.membersLoadFailed')
  return ''
}

// The avatar row may only claim "nobody here" from a LOADED roster.
// Hiding it while `_agents` is absent converts an unread roster into a
// confident empty state — the same defect one layer down.
export const teamAvatarsHidden = (root) => {
  if (teamSplitUnknown(root)) return false
  return root.people.length + _agentRoster(root).length === 0
}

// Mixed people+agents sample for the avatar row. Not a count, so it may
// render whatever IS loaded — but it reads the roster through the same
// helper so no raw `_agents.length` read survives on this surface.
export const teamAvatarRows = (root) => {
  const people = _peopleLoaded(root) ? root.people.slice(0, 3) : []
  return [...people, ..._agentRoster(root).slice(0, 3)].slice(0, 5)
}

const _shellSlugify = (name) =>
  String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)

// Access presets for new agents — the user-role the agent operates with
// and the permission set it implies. Mirrors the workspace member-role
// vocabulary (shared SelectRoleField: guest | editor | admin) so agents
// slot into the same mental model as people. The permission keys are
// stored on the agent (User.metadata.permissions) for the orchestrator /
// server to enforce.
export const AGENT_ACCESS_ROLES = [
  {
    value: 'guest',
    text: 'workspace.agents.role.guest',
    permissions: ['read'],
    hint: 'workspace.agents.role.guestHint'
  },
  {
    value: 'editor',
    text: 'workspace.agents.role.editor',
    permissions: ['read', 'write'],
    hint: 'workspace.agents.role.editorHint'
  },
  {
    value: 'admin',
    text: 'workspace.agents.role.admin',
    permissions: ['read', 'write', 'manage'],
    hint: 'workspace.agents.role.adminHint'
  }
]

export const agentAccessOptions = () => AGENT_ACCESS_ROLES

// Permission summary for the currently-selected access level — rendered
// reactively under the Access select in CreateAgentModal.
export const agentAccessHint = (root) => {
  const v = root?._createAgentAccess || 'editor'
  const r = AGENT_ACCESS_ROLES.find((x) => x.value === v)
  return (r && r.hint) || ''
}

// Project options for the "Runs in" scope select — the active workspace's
// projects (same filter as the home Projects widget: _projectsForActiveOrg
// scoped to the active workspace id), with a leading whole-workspace
// option (empty value = unscoped agent).
export const agentProjectOptions = (root) => {
  const all = Array.isArray(root?._projectsForActiveOrg) ? root._projectsForActiveOrg : []
  const activeWsId = root?.jwtClaims?.active_workspace_id || root?.workspace?.id
  const scoped = !activeWsId
    ? all
    : all.filter((p) => {
        const wsId = p?.workspace?.id || p?.workspace?._id || p?.workspace
        return String(wsId) === String(activeWsId)
      })
  return [
    { value: '', text: 'workspace.agents.scope.wholeWorkspace' },
    ...scoped
      .map((p) => ({
        value: String(p.id || p._id || p.key || ''),
        text: p.name || p.key || 'workspace.agents.untitledProject'
      }))
      .filter((o) => o.value)
  ]
}

const _resetCreateState = {
  _createAgentOpen: false,
  _createAgentName: '',
  _createAgentRole: '',
  _createAgentAccess: 'editor',
  _createAgentProjectId: '',
  _createAgentPending: false,
  _createAgentError: null
}

export const closeCreateAgent = (root) => root?.update?.(_resetCreateState)

// Server-backed create. Routes through sdk.createAgent — the agents API
// owns the User(kind:'agent') write. Feature-detected so the UI degrades
// gracefully on builds where the create endpoint hasn't shipped yet (the
// conversational + listAgents APIs already exist). On success the new
// agent is optimistically appended to root._agents so it shows
// immediately on the home row + the embedded Team & agents canvas;
// _agentsLoaded:false lets the canvas re-sync authoritatively from the
// server on its next mount.
export const submitCreateAgent = async (el, root) => {
  if (!root?.update) return
  const name = String(root._createAgentName || '').trim()
  if (!name) return
  const role = String(root._createAgentRole || '').trim()
  root.update({ _createAgentPending: true, _createAgentError: null })

  const sdk = el?.sdk || root?.sdk
  if (!sdk || typeof sdk.createAgent !== 'function') {
    root.update({
      _createAgentPending: false,
      _createAgentError: el.call('polyglot', 'workspace.createAgent.unavailable')
    })
    return
  }

  // Access level → stored user-role + permission set; project scope is
  // the workspace-project id the agent runs in ('' = whole workspace).
  const access =
    AGENT_ACCESS_ROLES.find((r) => r.value === (root._createAgentAccess || 'editor')) ||
    AGENT_ACCESS_ROLES[1]
  const projectId = String(root._createAgentProjectId || '').trim() || null

  try {
    const created = await sdk.createAgent({
      name,
      role,
      kind: 'agent',
      userRole: access.value,
      permissions: access.permissions,
      projectId,
      // Org tenancy (2026-06-06) — agents are created per workspace
      // org; the server stamps metadata.organization and scopes
      // listAgents by it.
      organization: root?.jwtClaims?.workspace_org_id || null
    })
    const c = (created && created.data) || created || {}
    const slug = String(c.username || c.slug || _shellSlugify(name) || name).toLowerCase()
    const newAgent = {
      id: slug,
      name: c.name || name,
      caption: role || el.call('polyglot', 'workspace.agents.fallbackName') + ' · ' + slug,
      kind: 'agent',
      avatar: c.avatar || null,
      userId: c.id || c._id || null,
      email: c.email || null
    }
    const existing = _agentRoster(root)
    const next = existing.some((a) => String(a.id || '').toLowerCase() === slug)
      ? existing
      : [...existing, newAgent]
    root.update({
      ..._resetCreateState,
      _agents: next,
      _agentsLoaded: false
    })

    // Auto-assign the new agent to the creator so it shows up in
    // "Your agents" immediately + renders an assignment edge on
    // /team-and-agents. Nika 2026-06-03: "new agents I create are by
    // default connected to me." Failure here is non-fatal — the agent
    // exists; the user can re-assign manually if the wire failed.
    const agentUserId = newAgent.userId
    const assigneeUserId = root.currentUserId
    const orgId =
      root.jwtClaims?.workspace_org_id ||
      (root.jwtClaims?.orgs && root.jwtClaims.orgs[0]?.id) ||
      null
    if (agentUserId && assigneeUserId && orgId) {
      try {
        await assignAgentToUser(root, { orgId, agentUserId, assigneeUserId })
      } catch (assignErr) {
        console.warn(
          '[submitCreateAgent] auto-assign to creator failed:',
          assignErr?.message || assignErr
        )
      }
    }
    // Created (the auto-assign above is best-effort): the caller may now take
    // its form as saved (markPageSaved).
    return true
  } catch (err) {
    root.update({
      _createAgentPending: false,
      _createAgentError: (err && err.message) || el.call('polyglot', 'workspace.createAgent.failed')
    })
    return false
  }
}
